import React, { useCallback, useRef, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import { IconButton, InputAdornment } from "@mui/material";

import { ClientSearchFiltersCheckboxLabel } from "../Clients/types";
import searchIcon from "@src/assets/svg/search_icon.svg";
import Popover from "@src/sharedComponents/Popover/Popover";
import ScrollableCheckboxList, {
  CheckboxListConfig,
} from "@src/sharedComponents/ScrollableCheckboxList/ScrollableCheckboxList";
import useResetTablePaginationPage from "@src/sharedComponents/Table/useResetTablePaginationPage";
import TextInput from "@src/sharedComponents/TextInput/TextInput";

import { popoverDropdownMenuWidth } from "./constants";

import * as styles from "./Households.module.css";

export function SearchInput({
  search,
  setSearch,
  searchFilters,
  setSearchFilters,
  placeholder = "Search clients...",
}: {
  search: string;
  setSearch: (search: string) => void;
  searchFilters: CheckboxListConfig<ClientSearchFiltersCheckboxLabel>;
  setSearchFilters: (
    filters: CheckboxListConfig<ClientSearchFiltersCheckboxLabel>
  ) => void;
  placeholder?: string;
}) {
  const filterPopoverAnchorRef = useRef<HTMLDivElement | null>(null);
  const [showFilterPopover, setShowFilterPopover] = useState<boolean>(false);
  const resetTablePaginationPage = useResetTablePaginationPage();

  const onChangeClientSearchFilters = (
    config: CheckboxListConfig<ClientSearchFiltersCheckboxLabel>
  ) => {
    if (Object.values(config).filter((val) => val.checked).length === 0) {
      // always have 1 filter checked
      return;
    }
    setSearchFilters(config);
  };

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      resetTablePaginationPage();
    },
    [resetTablePaginationPage, setSearch]
  );
  const handleClearOnSearch = () => {
    setSearch("");
    resetTablePaginationPage();
  };

  const toggleFilterPopover = (show: boolean) => () => {
    setShowFilterPopover(show);
  };

  return (
    <>
      <div ref={filterPopoverAnchorRef}>
        <TextInput
          label=""
          value={search}
          onChange={handleOnChange}
          placeholder={placeholder}
          style={{ width: "260px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <img src={searchIcon} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {search && (
                  <IconButton
                    aria-label="clear search"
                    onClick={handleClearOnSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon fontSize="small" className={styles.clearIcon} />
                  </IconButton>
                )}
                <IconButton
                  aria-label="filter search"
                  onClick={toggleFilterPopover(!showFilterPopover)}
                  edge="end"
                  size="small"
                >
                  <FilterListIcon className={styles.filterIcon} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>

      <Popover
        open={showFilterPopover}
        onClose={toggleFilterPopover(false)}
        anchor={filterPopoverAnchorRef.current}
        transformOriginVertical={-4}
        anchorOriginHorizontal="right"
        transformOriginHorizontal="right"
        popoverElement={null}
      >
        <ScrollableCheckboxList
          haveSelectAll={false}
          checkboxesConfig={searchFilters}
          setCheckboxesConfig={onChangeClientSearchFilters}
          multiSelectOff={false}
          minWidth={popoverDropdownMenuWidth}
        />
      </Popover>
    </>
  );
}
